import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

// APIレスポンスの型
type OperationPackagesResponse = ApiResponseBody<
  "/v2/it-operation-core/operation-packages",
  "get"
>;
type PackageType =
  OperationPackagesResponse[keyof OperationPackagesResponse][number]["type"];

/**
 * オペレーションパッケージ一覧を取得する
 */
export async function listItOperationPackages() {
  const res = await apiClient.GET("/v2/it-operation-core/operation-packages");
  return getDataOrThrowApiError(res);
}

/**
 * オペレーションパッケージ一覧を取得するReact Queryフック
 */
export function useItOperationPackages() {
  return useQuery({
    queryKey: ["operation-packages"],
    queryFn: listItOperationPackages,
  });
}

/**
 * 指定されたパッケージタイプのパッケージIDを取得するReact Queryフック
 * @param packageType 取得したいパッケージのタイプ
 */
export function useItOperationPackageIdByType(packageType: PackageType) {
  // 全パッケージを取得
  // TODO: 作るならOperationPackage全体の共通エンドポイント作った方が良さそう:w

  const { data: allPackages, error, isLoading } = useItOperationPackages();

  // パッケージタイプに対応するIDをメモ化して算出
  const packageId = useMemo(() => {
    if (!allPackages || !packageType) return null;

    // 各グループからパッケージを検索
    for (const group of Object.values(allPackages)) {
      const targetPackage = group.find((pkg) => pkg.type === packageType);
      if (targetPackage) {
        return targetPackage.id;
      }
    }
    return null;
  }, [allPackages, packageType]);

  return {
    data: packageId,
    error,
    isLoading,
    isSuccess: !isLoading && !error,
    isError: !!error,
  };
}

// パッケージタイプの型をエクスポート
export type { PackageType };
